/**
 * The app window from the prototype: the violet ground, and on it the 264px
 * sidebar region, the main region, and — when a chat opens a draft — the
 * drafts region, 16px apart. Under 760px the gutters go, the sidebar becomes
 * a drawer over a scrim, and a hamburger appears in the header. There the
 * window keeps clear of a notch at the top and sides; the pages with a
 * composer keep clear of the bottom themselves.
 *
 * Owns what every page shares: the theme (one `useTheme`, so the toggle and
 * the root agree), Ctrl/Cmd+K search, and the drawer. Pages reach it through
 * `useShell()` for the breakpoint, the drawer and the drafts region's host
 * element, which the chat page portals its panel into so the panel is a
 * sibling region rather than something inside the chat.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useViewport } from '../hooks/useViewport';
import { useRouter } from '../router';
import { useTheme } from '../theme';
import { SearchDialog } from './SearchDialog';
import { Sidebar } from './Sidebar';
import { Ph } from './ui';

interface ShellValue {
  width: number;
  narrow: boolean;
  /** Under 480px: a phone held upright. */
  compact: boolean;
  /** The main pointer is a finger. */
  touch: boolean;
  openDrawer: () => void;
  /** Where the drafts region mounts, beside the main region. */
  panelHost: HTMLElement | null;
}

const ShellContext = createContext<ShellValue | null>(null);

export function useShell(): ShellValue {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell used outside Shell');
  return ctx;
}

/** What `useShell()` answers for a page drawn without the shell, in a narrow
 *  pane (`/panel`): always narrow, with no drawer and no drafts region. */
export function BareShell({ children }: { children: React.ReactNode }) {
  const { width, compact, touch } = useViewport();
  const value = useMemo<ShellValue>(
    () => ({ width, narrow: true, compact, touch, openDrawer: () => undefined, panelHost: null }),
    [width, compact, touch]
  );
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

/** The main region's 52px header row. Empty on most pages but for the
 *  hamburger on a phone; the chat page fills it with its title. */
export function MainHeader({ children }: { children?: React.ReactNode }) {
  const { narrow, openDrawer } = useShell();
  return (
    <div
      style={{
        flex: 'none',
        height: 52,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 12px 0 16px',
        minWidth: 0
      }}
    >
      {narrow ? (
        <button
          type="button"
          className="bare"
          aria-label="Open menu"
          onClick={openDrawer}
          style={{
            width: 44,
            height: 44,
            marginLeft: -8,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--text-strong)'
          }}
        >
          <Ph name="list" size={20} />
        </button>
      ) : null}
      {children}
    </div>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  const { width, narrow, compact, touch } = useViewport();
  const { path } = useRouter();
  const [theme, toggleTheme] = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [panelHost, setPanelHost] = useState<HTMLElement | null>(null);

  // Going anywhere closes the drawer, as does growing out of phone width.
  useEffect(() => setDrawerOpen(false), [path, narrow]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setDrawerOpen(false);
        setSearchOpen(true);
      } else if (e.key === 'Escape') {
        setDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo<ShellValue>(
    () => ({ width, narrow, compact, touch, openDrawer: () => setDrawerOpen(true), panelHost }),
    [width, narrow, compact, touch, panelHost]
  );

  const showNav = !narrow || drawerOpen;

  return (
    <ShellContext.Provider value={value}>
      <div
        className="mv-app"
        data-theme={theme}
        style={{
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          gap: narrow ? 0 : 16,
          padding: narrow ? 'env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left)' : 16,
          background: 'var(--app-ground)',
          color: 'var(--text-body)',
          fontFamily: 'var(--font-body)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {narrow && drawerOpen ? (
          <div
            aria-hidden="true"
            className="scrim-in"
            onClick={() => setDrawerOpen(false)}
            style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'rgba(8,6,16,.5)' }}
          />
        ) : null}

        {showNav ? (
          <Sidebar
            drawer={narrow}
            touch={touch}
            theme={theme}
            onToggleTheme={toggleTheme}
            onSearch={() => {
              setDrawerOpen(false);
              setSearchOpen(true);
            }}
            onNavigate={() => setDrawerOpen(false)}
          />
        ) : null}

        <main
          style={{
            flex: 1,
            minWidth: 0,
            // Between the sidebar and the panel, unless the panel goes first.
            order: 1,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--app-panel)',
            borderRadius: narrow ? 0 : 'var(--radius-region)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {children}
        </main>

        {/* The drafts region. `display: contents` so what the chat page
            portals in is a flex item of the window, like the sidebar. */}
        <div ref={setPanelHost} style={{ display: 'contents' }} />

        {searchOpen ? <SearchDialog onClose={() => setSearchOpen(false)} /> : null}
      </div>
    </ShellContext.Provider>
  );
}
